/// <reference types="vite/client" />
/**
 * Thin client for the admin-llm-proxy Worker.
 *
 * The proxy URL is read from VITE_LLM_PROXY_URL with a workers.dev fallback.
 * The browser never sees the LiteLLM master key — that lives only in the
 * Worker as a wrangler secret.
 */

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export const PROXY_URL: string =
  (import.meta.env.VITE_LLM_PROXY_URL as string | undefined) ??
  "https://admin-llm-proxy.will-i-bracken.workers.dev";

export interface StreamChatOptions {
  signal?: AbortSignal;
}

/**
 * Stream a chat completion from the proxy. Calls onChunk for each delta token,
 * onDone with the final aggregated text, and onError on failure.
 * Returns a promise that resolves when the stream completes.
 */
export async function streamChat(
  messages: ChatMessage[],
  model: string,
  onChunk: (delta: string) => void,
  onDone: (full: string) => void,
  onError?: (err: { status?: number; message: string }) => void,
  opts: StreamChatOptions = {},
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${PROXY_URL}/v1/chat/completions`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
      body: JSON.stringify({ model, messages, stream: true }),
      signal: opts.signal,
    });
  } catch (e: any) {
    onError?.({ message: e?.message ?? "Network error" });
    return;
  }

  if (!res.ok || !res.body) {
    let detail = "";
    try { detail = await res.text(); } catch {}
    onError?.({ status: res.status, message: detail || res.statusText });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let full = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by blank lines; events may span multiple "data:" lines.
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (payload === "[DONE]") {
          onDone(full);
          return;
        }
        try {
          const parsed = JSON.parse(payload);
          const delta: string | undefined = parsed?.choices?.[0]?.delta?.content;
          if (delta) {
            full += delta;
            onChunk(delta);
          }
        } catch {
          // ignore non-JSON keepalive frames
        }
      }
    }
    onDone(full);
  } catch (e: any) {
    onError?.({ message: e?.message ?? "Stream read error" });
  }
}

export interface ModelEntry { id: string; [k: string]: unknown; }

export async function listModels(): Promise<ModelEntry[]> {
  const res = await fetch(`${PROXY_URL}/v1/models`);
  if (!res.ok) throw new Error(`models ${res.status}`);
  const j = await res.json() as { data?: ModelEntry[] };
  return j.data ?? [];
}
